import React, { Suspense, useEffect } from 'react'
import Subnav from '@fs/zion-tree-subnav-legacy'
import { NotFound, Route, Switch, useParams } from '@fs/zion-router'
import { useContainerWidth } from '@fs/zion-ui'
import ErrorBoundary from '@fs/zion-error-boundary'
import { GroupsProvider, useGroupsContext, setLastViewedPid, setLastViewedPersonView } from '@fs/zion-tree-groups'
import TreeRoute from '@fs/zion-tree-route'
import { SWRConfig } from 'swr'
import { usePageViewEvent } from '@fs/zion-analytics'
import { pidRegexStr } from '@fs/zion-pid'
import RedirectToDetails from './helpers/RedirectToDetails'
import PageContentWidthContext, { sizes } from './person-page/PageContentWidthContext'
import PersonAppProviders from './providers/PersonAppProviders'
import LargeLoadingSpinner from './common/LargeLoadingSpinner'
import useGroupOverlay from './helpers/useGroupOverlay'
import ShowTempleProvider from './providers/ShowTempleContext'
import usePageDetailPrefix from './hooks/usePageDetailPrefix'

const Merge = React.lazy(() => import('./merge/MergePage'))
const ChangeLog = React.lazy(() => import('./changelog/ChangeLog'))
const PersonPage = React.lazy(() => import('./person-page/PersonPage'))
const ResearchHelp = React.lazy(() => import('./research-help/ResearchHelp'))
const DevPage = React.lazy(() => import('./dev-page/DevPage'))
const PersonPrintView = React.lazy(() => import('./person-print-view/PersonPrintView'))
const MergeAnalysis = React.lazy(() => import('./merge-analysis/MergeAnalysis'))
const SplitAnalysis = React.lazy(() => import('./split-analysis/SplitAnalysis'))
const QualityScoreSegments = React.lazy(() => import('./quality-score-segments/QualityScoreSegments'))

const subPages = [
  'details',
  'ordinances',
  'about',
  'sources',
  'memories',
  'vitals',
  'non-vitals',
  'family',
  'tools',
  'search-records',
  'collaborate',
  'timeline',
]

const PersonPageWrapper = (): JSX.Element => {
  useGroupOverlay()
  const { subPage, pid } = useParams()
  const { currentTreeId } = useGroupsContext()
  const sendPageView = usePageViewEvent()
  const pageDetailPrefix = usePageDetailPrefix()

  // page_detail updates link_page for all analytics past this point
  useEffect(() => {
    if (pageDetailPrefix !== undefined) {
      sendPageView({ page_detail: `${pageDetailPrefix}Person:${subPage}` })
    }
  }, [pageDetailPrefix, sendPageView, subPage])

  useEffect(() => {
    if (currentTreeId) {
      setLastViewedPid('')
    } else {
      setLastViewedPid(pid)
    }
  }, [pid, currentTreeId])

  useEffect(() => {
    subPage && setLastViewedPersonView(subPage)
  }, [subPage])

  return <PersonPage />
}

export default function App(): JSX.Element {
  const atWidth = useContainerWidth()

  return (
    <SWRConfig value={{ revalidateOnFocus: process.env.NODE_ENV !== 'development' }}>
      <ErrorBoundary>
        <GroupsProvider>
          <ShowTempleProvider>
            <PageContentWidthContext.Provider value={atWidth(sizes)}>
              {!window.location.pathname.includes('/print-view/') && <Subnav page="person" showHelpOthers />}
              {/* This app has <Subnav> inside <main>. This div with id matching the skipLinkId in index.js tells screen readers to skip the subnav and go to the main content */}
              <div id="skipToMainContent" />
              <Suspense fallback={<LargeLoadingSpinner />}>
                <Switch>
                  {/* Checking NODE_ENV here guarantees that this will be stripped out in the production build. */}
                  {process.env.NODE_ENV === 'development' && <Route exact path="/" component={DevPage} />}

                  {/* /tree/person/research-help/ */}
                  <TreeRoute exact path={`/research-help/:pid(${pidRegexStr})`}>
                    <PersonAppProviders>
                      <ResearchHelp />
                    </PersonAppProviders>
                  </TreeRoute>

                  {/* /tree/person/changelog/ */}
                  <TreeRoute
                    exact
                    path={[
                      `/changelog/:pid(${pidRegexStr})/:relationshipType(parent-child|couple|association)/:relationshipId`,
                      `/changelog/:pid(${pidRegexStr})/:filterType?`,
                    ]}
                  >
                    <ChangeLog />
                  </TreeRoute>

                  {/* /tree/person/changelog/merge-analysis/ */}
                  <TreeRoute
                    exact
                    path={[`/changelog/merge-analysis/:survivorPid(${pidRegexStr})/:duplicatePid(${pidRegexStr})`]}
                  >
                    <MergeAnalysis />
                  </TreeRoute>

                  {/* /tree/person/changelog/split-analysis/ */}
                  <TreeRoute
                    exact
                    path={[`/changelog/split-analysis/:combinedPid(${pidRegexStr})/:extractedPid(${pidRegexStr})`]}
                  >
                    <SplitAnalysis />
                  </TreeRoute>

                  {/* /quality-score/segments */}
                  <TreeRoute exact path="/quality-score/segments">
                    <QualityScoreSegments />
                  </TreeRoute>

                  {/* /tree/person/merge/ */}
                  <TreeRoute
                    exact
                    path={`/merge/:mergeStep(verify|compose|confirm)/:survivorPid(${pidRegexStr})/:duplicatePid(${pidRegexStr})`}
                  >
                    <Merge />
                  </TreeRoute>

                  {/* Person Subpages (details, ordinances, about, sources, etc.) */}
                  <TreeRoute exact path={`/:subPage(${subPages.join('|')})/:pid(${pidRegexStr})`}>
                    <PersonAppProviders>
                      <PersonPageWrapper />
                    </PersonAppProviders>
                  </TreeRoute>

                  {/* Redirect various paths to the person details view (or entered subpage) */}
                  <TreeRoute exact path={[`/:pid(${pidRegexStr})`, `/:subPage(${subPages.join('|')})`, '/']}>
                    <RedirectToDetails />
                  </TreeRoute>

                  <TreeRoute exact path={`/print-view/:pid(${pidRegexStr})/`}>
                    <PersonAppProviders>
                      <PersonPrintView />
                    </PersonAppProviders>
                  </TreeRoute>

                  {/* 404 Not Found */}
                  <Route path="*">
                    <NotFound />
                  </Route>
                </Switch>
              </Suspense>
            </PageContentWidthContext.Provider>
          </ShowTempleProvider>
        </GroupsProvider>
      </ErrorBoundary>
    </SWRConfig>
  )
}
